import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Search, Trash2 } from 'lucide-react';
import { useUIColors } from '../../../../hooks/useTheme';

const BULK_TONES = {
  danger: { background: '#dc2626', color: '#fff' },
  success: { background: '#16a34a', color: '#fff' },
};

const compareValues = (a, b, dir) => {
  const mult = dir === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * mult;
  return String(a ?? '').localeCompare(String(b ?? ''), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Tabla de Tesorería con el mismo look que el Libro de ventas (AccountingRegisterTable3).
 * `columns[].sortValue(row)` habilita el orden; `filters[].predicate(row, value)` filtra por select.
 */
const TreasuryTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  title,
  subtitle,
  columns = [],
  defaultSort = null,
  searchPlaceholder = 'Buscar…',
  searchText = () => '',
  filters = [],
  tabs = null,
  headerActions = [],
  bulkActions = [],
  onEdit,
  onDelete,
  onRowClick,
  deleteLabel = 'Eliminar',
  deleteConfirmText,
  emptyText = 'Sin registros aún.',
  minTableWidth = 920,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const inputBg = ui.inputBg || ui.panel || bg;
  const inputBorder = ui.inputBorder || border;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [sortColumn, setSortColumn] = useState(defaultSort?.key || null);
  const [sortDir, setSortDir] = useState(defaultSort?.dir || 'desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const headerSelectRef = useRef(null);

  const selectStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  };
  const filterLabelStyle = { fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' };
  const outlineBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 36,
    padding: '0 14px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  };

  useEffect(() => {
    const available = new Set(rows.map((row) => row.id));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => available.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      for (const filter of filters) {
        const value = filterValues[filter.key];
        if (value && !filter.predicate(row, value)) return false;
      }
      if (!q) return true;
      return String(searchText(row) || '').toLowerCase().includes(q);
    });
  }, [rows, searchQuery, filterValues, filters, searchText]);

  const sortedRows = useMemo(() => {
    const column = columns.find((col) => col.key === sortColumn);
    if (!column?.sortValue) return filteredRows;
    return [...filteredRows].sort(
      (a, b) =>
        compareValues(column.sortValue(a), column.sortValue(b), sortDir) ||
        compareValues(Number(a.id) || 0, Number(b.id) || 0, sortDir)
    );
  }, [filteredRows, columns, sortColumn, sortDir]);

  const showBulkChrome = bulkActions.length > 0;
  const visibleIds = sortedRows.map((row) => row.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const handleSortClick = (column) => {
    if (!column.sortValue) return;
    if (sortColumn === column.key) setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(column.key);
      setSortDir(column.defaultDir || 'asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (!column.sortValue) return null;
    if (sortColumn !== column.key) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const hasActiveFilters = Boolean(searchQuery.trim()) || filters.some((f) => filterValues[f.key]);
  const clearFilters = () => {
    setSearchQuery('');
    setFilterValues({});
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    else setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };
  const toggleRow = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const runBulkAction = async (action) => {
    const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
    if (!selectedRows.length) return;
    if (action.confirm && !window.confirm(action.confirm(selectedRows.length))) return;
    const ok = await action.onClick(selectedRows);
    if (ok !== false) setSelectedIds([]);
  };

  const hasRowActions = Boolean(onEdit || onDelete);
  const colCount = columns.length + (showBulkChrome ? 1 : 0) + (hasRowActions ? 1 : 0);

  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden', background: bg }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>{title}</h3>
          <p style={{ margin: 0, fontSize: 12, color: muted }}>
            {loading ? 'Cargando datos...' : error || subtitle}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {headerActions.map((action) => {
            const Icon = action.icon;
            const primary = action.variant === 'primary';
            return (
              <button
                key={action.key}
                type="button"
                onClick={() => action.onClick(sortedRows)}
                disabled={action.disabled || action.busy}
                title={action.title}
                style={{
                  ...outlineBtn,
                  ...(primary ? { border: 'none', background: '#16a34a', color: '#fff' } : null),
                  cursor: action.busy ? 'wait' : action.disabled ? 'not-allowed' : 'pointer',
                  opacity: action.busy || action.disabled ? 0.7 : 1,
                }}
              >
                {Icon ? <Icon size={16} /> : null} {action.busy ? action.busyLabel || action.label : action.label}
                {action.badge ? (
                  <span
                    style={{
                      minWidth: 20,
                      height: 20,
                      padding: '0 6px',
                      borderRadius: 999,
                      background: '#d97706',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    {action.badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {tabs && tabs.items.length > 1 ? (
        <div
          role="tablist"
          aria-label={tabs.ariaLabel}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
            overflowX: 'auto',
          }}
        >
          {tabs.items.map((tab) => {
            const active = tabs.active === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                title={tab.fullLabel || tab.label}
                onClick={() => tabs.onChange(tab.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? text : muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{tab.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
          <span style={filterLabelStyle}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        {filters.map((filter) => (
          <label
            key={filter.key}
            style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: filter.minWidth || 150 }}
          >
            <span style={filterLabelStyle}>{filter.label}</span>
            <select
              value={filterValues[filter.key] || ''}
              onChange={(event) =>
                setFilterValues((prev) => ({ ...prev, [filter.key]: event.target.value }))
              }
              style={selectStyle}
            >
              <option value="">Todos</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{ ...selectStyle, padding: '0 12px', cursor: 'pointer' }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 13, color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          {bulkActions.map((action) => {
            const Icon = action.icon;
            const tone = BULK_TONES[action.tone] || {
              background: inputBg,
              color: text,
              border: `1px solid ${inputBorder}`,
            };
            return (
              <button
                key={action.key}
                type="button"
                onClick={() => runBulkAction(action)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 32,
                  padding: '0 12px',
                  borderRadius: 6,
                  border: 'none',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  ...tone,
                }}
              >
                {Icon ? <Icon size={14} /> : null} {action.label}
              </button>
            );
          })}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: minTableWidth }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: column.align || 'left',
                    fontSize: 11,
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: column.sortValue ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(column)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {column.label}
                    <SortIcon column={column} />
                  </span>
                </th>
              ))}
              {hasRowActions ? <th style={{ padding: `${thPadV}px ${padH}px` }} /> : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                  Cargando registros…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                  {error || (hasActiveFilters ? 'Ningún registro coincide con los filtros.' : emptyText)}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const selected = selectedIds.includes(row.id);
                const rowBg = selected ? `color-mix(in srgb, ${ui.interactive} 8%, ${bg})` : bg;
                return (
                  <tr
                    key={row.id}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.background = ui.rowHover || headerBg;
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.background = rowBg;
                    }}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: rowBg,
                      cursor: onRowClick || onEdit ? 'pointer' : 'default',
                    }}
                    onClick={() => (onRowClick || onEdit)?.(row)}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRow(row.id)}
                          aria-label="Seleccionar fila"
                        />
                      </td>
                    ) : null}
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        style={{
                          padding: `${padV}px ${padH}px`,
                          textAlign: column.align || 'left',
                          fontSize: 12,
                          color: text,
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                        }}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                    {hasRowActions ? (
                      <td style={{ padding: `${padV}px ${padH}px` }} onClick={(event) => event.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          {onEdit ? (
                            <button
                              type="button"
                              onClick={() => onEdit(row)}
                              style={{
                                height: 28,
                                width: 28,
                                borderRadius: 8,
                                border: `1px solid ${border}`,
                                background: 'transparent',
                                color: text,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              aria-label="Editar"
                            >
                              <Pencil size={13} />
                            </button>
                          ) : null}
                          {onDelete ? (
                            <button
                              type="button"
                              onClick={() => {
                                if (deleteConfirmText && !window.confirm(deleteConfirmText(row))) return;
                                onDelete(row);
                              }}
                              style={{
                                height: 28,
                                width: 28,
                                borderRadius: 8,
                                border: `1px solid ${ui.danger || '#dc2626'}`,
                                background: 'transparent',
                                color: ui.danger || '#dc2626',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              aria-label={deleteLabel}
                            >
                              <Trash2 size={13} />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TreasuryTable3;
